import { useEffect, useMemo, useState } from "react";
import useUserCredentials from "../../../../utils/hooks/user/useUserCredentials";
import {
  listResearcherCertifications,
  createResearcherCertification,
  deleteResearcherCertification
} from "../../../../utils/api/endpoints/researcher/profile";
import SecondaryButton from "../../../buttons/SecondaryButton";

const CertificatesManager = () => {
  const { id: currentUserId } = useUserCredentials();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [form, setForm] = useState<{
    certification_name: string;
    issuing_organization: string;
    issue_date: string;
    expiry_date?: string | null;
    logo_url?: string;
  }>({
    certification_name: "",
    issuing_organization: "",
    issue_date: "",
    expiry_date: "",
    logo_url: ""
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    if (!currentUserId) return;
    listResearcherCertifications(currentUserId)
      .then(res => {
        setItems(res.certifications || []);
      })
      .finally(() => setLoading(false));
  }, [currentUserId]);

  const resetForm = () => {
    setForm({ certification_name: "", issuing_organization: "", issue_date: "", expiry_date: "", logo_url: "" });
    setEditingId(null);
  };

  const refresh = async () => {
    if (!currentUserId) return;
    const res = await listResearcherCertifications(currentUserId);
    setItems(res.certifications || []);
  };

  const onCreate = async () => {
    if (!currentUserId) return;
    setSaving(true);
    try {
      await createResearcherCertification(form, currentUserId);
      await refresh();
      resetForm();
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!currentUserId) return;
    await deleteResearcherCertification(id, currentUserId);
    await refresh();
  };

  const onEdit = (item: any) => {
    setEditingId(item.certification_id || item.id);
    setForm({
      certification_name: item.certification_name || "",
      issuing_organization: item.issuing_organization || "",
      issue_date: item.issue_date ? String(item.issue_date).substring(0,10) : "",
      expiry_date: item.expiry_date ? String(item.expiry_date).substring(0,10) : "",
      logo_url: item.logo_url || ""
    });
    setShowForm(true);
  };

  const onUpdate = async () => {
    if (!currentUserId || !editingId) return;
    setSaving(true);
    try {
      // Reuse create endpoint if backend handles upsert; otherwise call update endpoint when available
      await deleteResearcherCertification(editingId, currentUserId);
      await createResearcherCertification(form, currentUserId);
      await refresh();
      resetForm();
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d?: string) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Manage Certifications</h2>
        {showForm
          ? (
            <SecondaryButton onClick={() => { setShowForm(false); resetForm(); }}>Cancel</SecondaryButton>
          )
          : (
            <SecondaryButton onClick={() => { resetForm(); setShowForm(true); }}>+ Add Certificate</SecondaryButton>
          )}
      </div>

      {showForm && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3">
            <h3 className="text-base font-semibold">{editingId ? "Edit Certificate" : "Add Certificate"}</h3>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Name*</label>
              <input className="border border-gray-300 rounded px-3 py-2 text-sm w-full" value={form.certification_name} onChange={e => setForm({ ...form, certification_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Organization*</label>
                <input className="border border-gray-300 rounded px-3 py-2 text-sm w-full" value={form.issuing_organization} onChange={e => setForm({ ...form, issuing_organization: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date*</label>
                <input type="date" className="border border-gray-300 rounded px-3 py-2 text-sm w-full" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (optional)</label>
                <input type="date" className="border border-gray-300 rounded px-3 py-2 text-sm w-full" value={form.expiry_date || ""} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (optional)</label>
                <input className="border border-gray-300 rounded px-3 py-2 text-sm w-full" value={form.logo_url || ""} onChange={e => setForm({ ...form, logo_url: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end">
              <SecondaryButton onClick={editingId ? onUpdate : onCreate} disabled={saving || !form.certification_name || !form.issuing_organization || !form.issue_date}>
                {editingId ? (saving ? "Updating..." : "Update Certificate") : (saving ? "Adding..." : "Add Certificate")}
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-500">No certifications added yet.</div>
        ) : (
          items.map(c => (
            <div key={c.certification_id || c.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-blue-600">
                  {/* Default medal icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M8 14l-2 6 6-3 6 3-2-6" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{c.certification_name}</div>
                  <div className="text-sm text-gray-600">{c.issuing_organization}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Issued {formatDate(c.issue_date)} {c.expiry_date ? ` • Expires ${formatDate(c.expiry_date)}` : " • No Expiration"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button className="text-blue-600 text-sm hover:underline" onClick={() => onEdit(c)}>Edit</button>
                <button className="text-red-600 text-sm hover:underline" onClick={() => onDelete(c.certification_id || c.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CertificatesManager;


